import React, { useEffect, useMemo, useState } from "react";

import PitchesService, { Pitch } from "@services/Pitches.service";
import { notification, Table } from "antd";
import { getPitchesColumns } from "../utils/const";
import Loader from "@appComponents/Loader/Loader";
import { DESKTOP_WIDTH } from "@bundles/consts";
import { useMediaQuery } from "react-responsive";
import { PitchMobileExpandedRow } from "./PitchMobileExpandedRow";
import { PitchDesktopExpandedRow } from "./PitchDesktopExpandedRow";
import { usePagination } from "@apps/MarketsApp/hooks/usePagination";
import { Button } from "@appComponents/Button";
import ChevronDownIcon from "../../../icons/chevron-down-black.svg";

type PitchesListProps = {
  searchQuery: string;
};

export const PitchesList = ({ searchQuery }: PitchesListProps) => {
  const isMobile = useMediaQuery({ maxWidth: DESKTOP_WIDTH });

  const { page, setPage, total, setTotal } = usePagination({
    initialPageSize: 20,
  });

  const [pitchesLoading, setPitchesLoading] = useState(true);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpandToggle = (record: Pitch) => {
    setExpandedRowKeys((prev) =>
      prev.includes(record.id)
        ? prev.filter((id) => id !== record.id)
        : [...prev, record.id]
    );
  };

  const pitchesColumns = useMemo(
    () =>
      getPitchesColumns({
        isMobile,
        handleExpandToggle,
        expandedRowKeys,
      }),
    [isMobile, handleExpandToggle, expandedRowKeys]
  );

  const fetchPitches = () => {
    PitchesService.getPitches({ page: page + 1 })
      .then((resp) => {
        setPitchesLoading(false);
        setPitches((prev) => [...prev, ...resp.data.pitches]);
        setPage((prev) => prev + 1);
      })
      .catch(() => {
        setPitchesLoading(false);
        notification.error({ message: "Something went wrong." });
      });
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPitchesLoading(true);
      PitchesService.getPitches({ page: 1, search_phrase: searchQuery })
        .then((resp) => {
          setExpandedRowKeys([]);
          setPitchesLoading(false);
          setPitches(resp.data.pitches);
          setPage(1);
          setTotal(resp.data.pagination.total_pages);
        })
        .catch(() => {
          setPitchesLoading(false);
          notification.error({ message: "Something went wrong." });
        });
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  if (pitchesLoading) {
    return <Loader />;
  }

  return (
    <div className="mt-[20px] md:mt-[50px]">
      <Table
        className="tailwind-table-white"
        columns={pitchesColumns}
        dataSource={pitches}
        pagination={false}
        rowKey="id"
        expandable={{
          expandedRowKeys,
          defaultExpandAllRows: false,
          showExpandColumn: false,
          expandedRowClassName: () => "bg-grey-light",
          expandedRowRender: (record) =>
            isMobile ? (
              <PitchMobileExpandedRow pitch={record} />
            ) : (
              <PitchDesktopExpandedRow pitch={record} />
            ),
        }}
      />

      {page < total && (
        <div className="flex justify-center mt-[40px]">
          <Button className="border-[#2EC973]" onClick={fetchPitches}>
            Load more
            <ChevronDownIcon className="ml-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
